<template>
  <DocumentsAISummaryChatMessage
    v-if="shouldDisplaySummaryMessage"
    :document-summary="requestedSummary!"
    with-text-animation
  />
  <DocumentsAISummaryMessageFromAI
    v-else
    :message-date="requestDate"
    class="summary"
    :class="{'request-error': shouldDisplayErrorMessage}"
  >
    {{ summaryMessageFromAi }}
  </DocumentsAISummaryMessageFromAI>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import { DocumentAISummary } from '@/project/documents/pane/DocumentsAISummaryPane.vue'
import aiSummariesService from '@/services/ai-summaries.service'

import DocumentsAISummaryChatMessage from './DocumentsAISummaryChatMessage.vue'
import DocumentsAISummaryMessageFromAI from './DocumentsAISummaryMessageFromAI.vue'

type DataType = {
  requestedSummary: DocumentAISummary | null
  requestState: QueryState,
  summaryMessageFromAi: string | null,
}

const TRANSLATION_KEY_PREFIX = 'project.documents.ai-summary.DocumentsAISummaryRequestChatMessage'

export default defineComponent({
  name: 'DocumentsAISummaryRequestChatMessage',
  components: { DocumentsAISummaryMessageFromAI, DocumentsAISummaryChatMessage },
  emits: ['error', 'success'],
  props: {
    documentId: {
      type: Number,
      required: true,
    },
    requestDate: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      requestedSummary: null as DataType['requestedSummary'],
      requestState: 'idle',
      summaryMessageFromAi: null,
    }
  },
  async mounted () {
    await this.requestSummary()
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    shouldDisplayErrorMessage () {
      return this.requestState === 'error'
    },
    shouldDisplaySummaryMessage () {
      return this.requestState === 'idle' && !!this.requestedSummary
    },
  },
  methods: {
    async requestSummary () {
      this.requestState = 'pending'
      this.summaryMessageFromAi = this.$t(`${TRANSLATION_KEY_PREFIX}.requestSummaryDefaultText`)
      
      try {
        const { data } = await aiSummariesService.requestDocumentAISummary(this.roomMnemo, this.documentId)
        this.requestedSummary = data as DocumentAISummary
        this.requestState = 'idle'
        this.$emit('success')
      } catch (error: any) {
        console.error(error)
        this.requestState = 'error'
        const errorCode = error.response?.data?.errorCode === 'TOO_FEW_WORDS_DETECTED' ? 'tooFewWords' : 'default'
        this.summaryMessageFromAi = this.$t(`${TRANSLATION_KEY_PREFIX}.errors.${errorCode}`)
        this.$emit('error')
      }
    },
  },
})
</script>

<style scoped lang="scss">
.request-error {
  :deep(.message-bubble) {
    background-color: rgb(var(--v-theme-error-lighten-1));
    border: 1px solid rgb(var(--v-theme-error));
  }
}
</style>